// Video file validation — gate the input boundary before a File enters the
// pipeline (drop zone or Replace picker in VideoPlayer.tsx).
//
// Rationale: downstream code (MP4Box demux, <video> preview, MP4 remux export)
// crashes with cryptic errors on bad input. We do cheap synchronous checks
// (size/MIME) first, then a brief async metadata probe via a hidden <video>
// element to validate codec playability + duration.
//
// Rules (per spec):
//   1. Container: video/mp4, video/webm, video/quicktime — reject others.
//   2. Size: hard-reject above 5 GB. Iterated up: 500MB (single-PUT era) →
//      2GB (single streamed PUT, but capped by mp4-muxer's in-memory
//      ArrayBufferTarget which hit V8's ~2GB ceiling) → 5GB (after
//      switching the upload mux to a streaming target that builds the
//      output Blob from many small Uint8Arrays — browsers disk-back
//      large Blobs, no single allocation has to fit the muxed output).
//      5GB is an artificial cap; technical headroom is much higher but
//      we don't want users dropping 25GB raw 4K masters into a browser
//      tool. Bump if real workflow needs more.
//   3. Codec: probe via canPlayType + load metadata. If <video> errors before
//      metadata loads, treat as unplayable codec → reject. (HEVC in Safari
//      is fine; HEVC in Chromium errors → caught here.)
//   4. Duration: must be > 0.05s. Some empty/corrupt MP4s have 0 duration.
//   5. Audio track: missing audio is a warning, not a rejection — the ducking
//      mixer just gets an empty bus.
//
// All rules use browser-native APIs only — no ffmpeg/codec libs.

const MAX_SIZE_BYTES = 5 * 1024 * 1024 * 1024;
const MIN_DURATION_SEC = 0.05;
const METADATA_TIMEOUT_MS = 5000;

const ACCEPTED_MIME_TYPES = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

const ACCEPTED_EXTENSIONS = new Set(['mp4', 'webm', 'mov']);

export type ValidationResult =
  | { ok: true; warnings: string[] }
  | { ok: false; error: string };

/** Best-effort container check. Some browsers report empty `file.type` for
 *  `.mov` files; fall back to extension sniffing in that case. */
function isAcceptedContainer(file: File): boolean {
  if (file.type && ACCEPTED_MIME_TYPES.has(file.type)) return true;
  const dot = file.name.lastIndexOf('.');
  if (dot < 0) return false;
  const ext = file.name.slice(dot + 1).toLowerCase();
  return ACCEPTED_EXTENSIONS.has(ext);
}

/** Format bytes as MB with 0–1 decimal place — used for the user-facing
 *  reject message so the limit is concrete. */
function formatMB(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  return mb >= 100 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
}

interface ProbeResult {
  duration: number;
  width: number;
  height: number;
  /** Browser flagged the resource as unplayable (codec mismatch most likely). */
  errored: boolean;
  /** Metadata timed out — caller treats as a soft reject. */
  timedOut: boolean;
  /** Best-effort: whether the decoded media has an audio track. Not all
   *  browsers expose this on a vanilla HTMLVideoElement; absent → assume
   *  audio is present (warning suppressed). */
  hasAudio: boolean | null;
}

/** Load `file` into a hidden <video> just long enough to read metadata.
 *  Resolves on `loadedmetadata`, `error`, or after METADATA_TIMEOUT_MS —
 *  whichever comes first. The objectURL is revoked in every exit path. */
function probeMetadata(file: File): Promise<ProbeResult> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    // Off-tree element — never attached to DOM, so it never paints.
    let settled = false;
    let timeoutId: number | null = null;

    const finish = (result: ProbeResult) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      try { v.removeAttribute('src'); v.load(); } catch { /* ignore */ }
      URL.revokeObjectURL(url);
      resolve(result);
    };

    v.addEventListener('loadedmetadata', () => {
      // Best-effort audio-track sniff. WebKit exposes `webkitAudioDecodedByteCount`,
      // Chromium exposes `mozHasAudio` historically (Firefox) and an
      // `audioTracks` collection (rarely populated). If none give a definite
      // answer, return null and the caller suppresses the warning.
      const ve = v as HTMLVideoElement & {
        mozHasAudio?: boolean;
        webkitAudioDecodedByteCount?: number;
        audioTracks?: { length: number };
      };
      let hasAudio: boolean | null = null;
      if (typeof ve.mozHasAudio === 'boolean') {
        hasAudio = ve.mozHasAudio;
      } else if (typeof ve.webkitAudioDecodedByteCount === 'number') {
        hasAudio = ve.webkitAudioDecodedByteCount > 0;
      } else if (ve.audioTracks && typeof ve.audioTracks.length === 'number') {
        // audioTracks.length === 0 is reliable; > 0 is reliable. Some browsers
        // simply don't populate the collection — covered by the null fallback
        // above.
        hasAudio = ve.audioTracks.length > 0 ? true : null;
      }
      finish({
        duration: v.duration,
        width: v.videoWidth,
        height: v.videoHeight,
        errored: false,
        timedOut: false,
        hasAudio,
      });
    });

    v.addEventListener('error', () => {
      finish({
        duration: 0,
        width: 0,
        height: 0,
        errored: true,
        timedOut: false,
        hasAudio: null,
      });
    });

    timeoutId = window.setTimeout(() => {
      finish({
        duration: 0,
        width: 0,
        height: 0,
        errored: false,
        timedOut: true,
        hasAudio: null,
      });
    }, METADATA_TIMEOUT_MS);

    v.src = url;
  });
}

/** Validate a user-supplied video File before it enters the pipeline. Cheap
 *  synchronous checks first (size, container) to fail fast on obvious
 *  rejects; only on success do we incur the metadata-probe round trip. */
export async function validateVideoFile(file: File): Promise<ValidationResult> {
  // 1. Container.
  if (!isAcceptedContainer(file)) {
    return {
      ok: false,
      error: 'Unsupported video format — use MP4, WebM, or MOV.',
    };
  }

  // 2. Size hard cap.
  if (file.size > MAX_SIZE_BYTES) {
    return {
      ok: false,
      error: `Video too large (${formatMB(file.size)}, limit 5 GB) — convert to smaller file before adding.`,
    };
  }
  if (file.size === 0) {
    return { ok: false, error: 'Video file is empty.' };
  }

  // 3+4+5. Async probe.
  const probe = await probeMetadata(file);

  if (probe.errored) {
    return {
      ok: false,
      error: 'Could not decode this video — the codec may not be supported by your browser (e.g. HEVC outside Safari).',
    };
  }
  if (probe.timedOut) {
    return {
      ok: false,
      error: 'Video metadata could not be read within 5s — the file may be corrupt.',
    };
  }
  if (!Number.isFinite(probe.duration) || probe.duration <= MIN_DURATION_SEC) {
    return {
      ok: false,
      error: 'Video has zero or near-zero duration — the file may be corrupt.',
    };
  }

  // 6. Codec advisory — `canPlayType` returning '' for the declared MIME
  // type means the browser thinks it can't play this container/codec combo.
  // We've already passed the metadata-load gate, so this is informational.
  const warnings: string[] = [];
  if (file.type) {
    const probe2 = document.createElement('video').canPlayType(file.type);
    if (probe2 === '') {
      warnings.push(
        `Browser reports it may not play ${file.type} reliably — preview/export could fail.`
      );
    }
  }

  if (probe.hasAudio === false) {
    warnings.push('Video has no audio track — ducking mixer will have an empty audio bus.');
  }

  return { ok: true, warnings };
}
